import * as React from "react";
import PropTypes from "prop-types";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import MDTypography from "components/MDTypography";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 350,
    },
  },
};

// helper: normalize incoming "value" to ids[]
function toIds(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((x) => (typeof x === "object" && x !== null ? x.id : x)).filter(Boolean);
}

function MultipleSelectCheckmarks({ optionsList, label, textValue, onSelection, height, value }) {
  // Controlled: selectionIds derived from value prop
  const selectionIds = React.useMemo(() => toIds(value), [value]);

  const handleChange = (event) => {
    // MUI can give string (autofill) or array
    const raw = event.target.value;
    const ids = typeof raw === "string" ? raw.split(",").map((x) => Number(x)) : raw;

    // Return selected objects (matches your current Admin schema using objects)
    const selectedObjects = optionsList.filter((o) => ids.includes(o.id));
    onSelection(selectedObjects);

    //If you prefer IDs instead, do:
    //onSelection(ids);
  };

  const renderValue = (ids) => {
    const selected = optionsList.filter((o) => ids.includes(o.id));
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {selected.map((o) => (
          <Chip key={o.id} label={o[textValue]} />
        ))}
      </Box>
    );
  };

  return (
    <FormControl fullWidth sx={{ minHeight: height || 50 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        height={height}
        multiple
        value={selectionIds}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={renderValue}
        MenuProps={MenuProps}
        sx={{
          // this controls the outer control height
          minHeight: height || 56,
          // this controls the inner area where chips render
          "& .MuiSelect-select": {
            minHeight: height || 56,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.5,
            paddingTop: "10px",
            paddingBottom: "10px",
          },
        }}
      >
        {optionsList.map((itemObj) => (
          <MenuItem key={itemObj.id} value={itemObj.id}>
            <Checkbox checked={selectionIds.includes(itemObj.id)} />
            <MDTypography variant="caption" color="text" fontWeight="regular">
              {itemObj[textValue]}
            </MDTypography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

MultipleSelectCheckmarks.propTypes = {
  optionsList: PropTypes.array.isRequired,
  onSelection: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  textValue: PropTypes.string.isRequired,
  height: PropTypes.number,
  value: PropTypes.array, // can be ids[] or objects[]
};

MultipleSelectCheckmarks.defaultProps = {
  value: [],
};

export default MultipleSelectCheckmarks;
